import React, { useContext, useState } from 'react';
import { IterationContext, IterationContextType } from '../../contexts/IterationContext';
import { Drag_n_drop } from 'Components/DesignSystem/Iconography/Icons';
import ConditionGroup from './ConditionGroup';

const IterationConditions: React.FC = () => {
    const {
        iterationPayload,
        activeIterationId,
        updateLogicalOperator,
        reorderConditions
    } = useContext<IterationContextType>(IterationContext);

    const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
    const [dragOverItemId, setDragOverItemId] = useState<string | null>(null);

    const currentIteration = iterationPayload?.iterations?.find(i => i.id === activeIterationId);

    if (!currentIteration) {
        return <div className="tw-text-ink-secondary">No iteration selected</div>;
    }

    const handleTopLevelLogicalOperatorChange = (conditionId: string, operator: string) => {
        if (activeIterationId) {
            updateLogicalOperator(
                activeIterationId,
                conditionId,
                operator as LogicalOperator
            );
        }
    };

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, conditionId: string) => {
        setDraggedItemId(conditionId);
        e.dataTransfer.setData('text/plain', conditionId);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>, conditionId: string) => {
        e.preventDefault();
        if (conditionId !== draggedItemId) {
            setDragOverItemId(conditionId);
        }
    };

    const handleDragEnd = () => {
        setDraggedItemId(null);
        setDragOverItemId(null);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetConditionId: string) => {
        e.preventDefault();

        if (draggedItemId && draggedItemId !== targetConditionId && activeIterationId) {
            const sourceIndex = currentIteration.conditions.findIndex(condition => condition.id === draggedItemId);
            const targetIndex = currentIteration.conditions.findIndex(condition => condition.id === targetConditionId);

            if (sourceIndex !== -1 && targetIndex !== -1) {
                reorderConditions(activeIterationId, sourceIndex, targetIndex);
            }
        }

        setDraggedItemId(null);
        setDragOverItemId(null);
    };

    return (
        <div className="tw-space-y-4 tw-pr-2">
            {currentIteration.conditions.map((condition, index) => (
                <React.Fragment key={condition.id}>
                    <div
                        className={`tw-flex tw-items-start tw-gap-3 ${draggedItemId === condition.id ? 'tw-opacity-50' : ''
                            } ${dragOverItemId === condition.id ? 'tw-border-t-2 tw-border-t-accent' : ''
                            }`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, condition.id)}
                        onDragOver={(e) => handleDragOver(e, condition.id)}
                        onDragEnd={handleDragEnd}
                        onDrop={(e) => handleDrop(e, condition.id)}
                        data-testid={`condition-row-${index}`}
                    >
                        <div className="tw-cursor-move tw-ml-3 tw-mt-8 tw-text-ink-tertiary tw-flex" data-testid={`condition-drag-handle-${index}`}>
                            <Drag_n_drop />
                        </div>
                        <div className="tw-flex-1">
                            <ConditionGroup
                                conditionNode={condition}
                                depth={0}
                                position={index}
                            />
                        </div>
                    </div>

                    {index < (currentIteration.conditions.length - 1) && (
                        <>
                            <div className="separator tw-mt-3" />
                            <div className="tw-px-4 tw-py-2">
                                <select
                                    /* Token classes, not inline colour: `color: "black"` here
                                       beat every stylesheet and stayed black in dark (§ 4.3). */
                                    className="form-select drop-down-dm tw-bg-surface tw-text-ink tw-border tw-border-solid tw-border-edge"
                                    value={condition.logical_operator}
                                    onChange={(e) => handleTopLevelLogicalOperatorChange(condition.id, e.target.value)}
                                    data-testid={`toplevel-logical-operator-${condition.id}`}
                                    style={{
                                        textAlign: "center",
                                        fontSize: ".8rem",
                                        fontWeight: "600",
                                        width: "80px"
                                    }}
                                >
                                    <option value="AND">And</option>
                                    <option value="OR">Or</option>
                                </select>
                            </div>
                            <div className="separator tw-mb-3" />
                        </>
                    )}
                </React.Fragment>
            ))}
        </div>
    );
};

export default IterationConditions;